/**
 * Locale types, detection, and persistence for the localization foundation.
 *
 * This module is framework-free and import-safe in any environment (browser,
 * jsdom, plain Node under Vitest): every DOM/storage touch is guarded, so a
 * blocked or throwing localStorage can never prevent the app from booting.
 *
 * Detection never writes to storage — only an explicit user selection
 * (persistLocale, called by setLanguage in ./index) persists a value.
 */

export type AppLocale = 'en' | 'es-419';

export const SUPPORTED_LOCALES: readonly AppLocale[] = ['en', 'es-419'];

export const DEFAULT_LOCALE: AppLocale = 'en';

/** localStorage key for the player's explicit language choice. */
export const LOCALE_STORAGE_KEY = 'app_language';

export function isAppLocale(value: unknown): value is AppLocale {
  return value === 'en' || value === 'es-419';
}

/**
 * Map one browser language tag to a supported locale, or null when the tag
 * is unsupported/malformed. Every Spanish primary subtag (es, es-419, es-US,
 * es-VE, es-MX, es-CO, es-ES, …) maps to 'es-419'; English variants map to
 * 'en'; everything else is null so detection can keep scanning.
 */
export function normalizeBrowserTag(tag: unknown): AppLocale | null {
  if (typeof tag !== 'string') return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/, 1)[0];
  if (primary === 'es') return 'es-419';
  if (primary === 'en') return 'en';
  return null;
}

/** Saved explicit preference, or null when absent/invalid/unreadable. */
export function readSavedLocale(): AppLocale | null {
  try {
    const raw = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY);
    return isAppLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Persist an explicit language selection. Callers other than setLanguage in
 * ./index should not need this. Failure (blocked storage) is silent — the
 * in-memory language still switches; it just won't survive a reload.
 */
export function persistLocale(locale: AppLocale): void {
  if (!isAppLocale(locale)) return;
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage unavailable — non-fatal by design.
  }
}

/** First supported locale from the browser's preference list, or null. */
export function browserLocale(): AppLocale | null {
  try {
    const nav = globalThis.navigator;
    if (!nav) return null;
    const candidates: readonly unknown[] =
      Array.isArray(nav.languages) && nav.languages.length > 0
        ? nav.languages
        : [nav.language];
    for (const tag of candidates) {
      const mapped = normalizeBrowserTag(tag);
      if (mapped) return mapped;
    }
  } catch {
    // Fall through to null.
  }
  return null;
}

/**
 * Resolve the locale to boot (or re-resolve) with, in priority order:
 *   1. valid saved local preference,
 *   2. account-level preference (future seam — callers pass it when account
 *      sync exists; nothing reads or stores it today),
 *   3. browser language,
 *   4. English.
 * Pure read — never writes to storage.
 */
export function detectLocale(accountLocale: AppLocale | null = null): AppLocale {
  return (
    readSavedLocale() ??
    (isAppLocale(accountLocale) ? accountLocale : null) ??
    browserLocale() ??
    DEFAULT_LOCALE
  );
}

/** Keep <html lang> in sync so screen readers pronounce the right language. */
export function applyDocumentLang(locale: AppLocale): void {
  try {
    const root = globalThis.document?.documentElement;
    if (root) root.lang = locale;
  } catch {
    // No DOM (tests, SSR-ish contexts) — nothing to sync.
  }
}
